/**
 * video-urls — detect + canonicalise embed URLs.
 *
 * Used by the AI-assist intake (P6) to:
 *   1. Decide which iframe provider a pasted URL belongs to (so the admin
 *      can show a provider chip: "YouTube", "Vimeo", "Bilibili", "Raw").
 *   2. Rewrite the user-pasted URL into the canonical embed URL the model is
 *      told to emit in the body (e.g. https://youtu.be/abc123…  →
 *      https://www.youtube.com/embed/abc123…). The public parser then
 *      validates the emitted src against the same trusted-domain allowlist.
 *
 * The two copies (server/lib + src/admin/lib) are deliberately duplicated
 * per the project's template-fields / preflight-gaps precedent — the two
 * tsconfigs have separate `rootDir` and a shared lib would either break the
 * server bundle or pull React into the server.
 *
 * Provider IDs:
 *   - 'youtube'   — youtube.com / youtu.be
 *   - 'vimeo'     — vimeo.com/<id>
 *   - 'bilibili'  — bilibili.com/video/(BV…|av…)
 *   - 'raw'       — caller-tagged URL that should be embedded as-is (e.g. a
 *                    /uploads/<uuid>.mp4 from cms_media; detectProvider
 *                    returns null for those, callers wrap in `raw`)
 *   - null        — URL doesn't match any known provider
 */

export type VideoProvider = 'youtube' | 'vimeo' | 'bilibili' | 'raw'

const YOUTUBE_ID = '[A-Za-z0-9_-]{11}'
const VIMEO_ID = '\\d{5,12}'
const BV_ID = 'BV[0-9A-Za-z]{10}'
const AV_ID = 'av\\d{1,12}'

// Order matters: check the shorter host forms (youtu.be) before the
// longer ones (youtube.com) so the regex doesn't double-match.
const RE_YOUTUBE_FULL = new RegExp(`youtube\\.com/watch\\?v=(${YOUTUBE_ID})`)
const RE_YOUTUBE_SHORT = new RegExp(`youtu\\.be/(${YOUTUBE_ID})`)
const RE_VIMEO = new RegExp(`vimeo\\.com/(${VIMEO_ID})`)
const RE_BILIBILI = new RegExp(`bilibili\\.com/video/(${BV_ID}|${AV_ID})`)

export function detectProvider(url: string): VideoProvider | null {
  if (!url || typeof url !== 'string') return null
  if (RE_YOUTUBE_FULL.test(url) || RE_YOUTUBE_SHORT.test(url)) return 'youtube'
  if (RE_VIMEO.test(url)) return 'vimeo'
  if (RE_BILIBILI.test(url)) return 'bilibili'
  return null
}

/** Extract the provider-specific id from a URL. Returns null if not a known provider. */
export function extractVideoId(url: string, provider: Exclude<VideoProvider, 'raw'>): string | null {
  if (!url) return null
  if (provider === 'youtube') {
    const m = url.match(RE_YOUTUBE_FULL) || url.match(RE_YOUTUBE_SHORT)
    return m ? m[1]! : null
  }
  if (provider === 'vimeo') {
    const m = url.match(RE_VIMEO)
    return m ? m[1]! : null
  }
  if (provider === 'bilibili') {
    const m = url.match(RE_BILIBILI)
    return m ? m[1]! : null
  }
  return null
}

/**
 * Rewrite a user-pasted URL into the canonical embed URL for the given
 * provider. For 'raw', returns the input unchanged. Returns null if the
 * input doesn't match the provider's URL shape.
 */
export function embedUrl(url: string, provider: VideoProvider): string | null {
  if (provider === 'raw') return url
  const id = extractVideoId(url, provider)
  if (!id) return null
  switch (provider) {
    case 'youtube':
      return `https://www.youtube.com/embed/${id}`
    case 'vimeo':
      return `https://player.vimeo.com/video/${id}`
    case 'bilibili':
      // Bilibili's official embed URL. ?autoplay=0 keeps the article-page
      // UX consistent (no surprise autoplay on scroll-into-view).
      return `https://player.bilibili.com/player.html?bvid=${id}&autoplay=0`
  }
}

/**
 * Trusted embed (hostname, path-prefix) pairs. The public ArticleDetail
 * parser uses this to decide whether an iframe in a ```html fence is safe
 * to render. The list is intentionally narrow — anything outside it is
 * dropped silently. Path-prefix matters: youtube.com/watch?v=… is NOT a
 * trusted embed (the /watch page would render its own UI inside our
 * iframe), only /embed/<id> is.
 */
const TRUSTED_EMBED_PATHS: Array<[hostname: string, pathPrefix: string]> = [
  ['www.youtube.com', '/embed/'],
  ['youtube.com', '/embed/'],
  ['youtube-nocookie.com', '/embed/'],
  ['player.vimeo.com', '/video/'],
  ['player.bilibili.com', '/player.html'],
]

export function isTrustedEmbedUrl(url: string): boolean {
  if (!url) return false
  try {
    const u = new URL(url)
    if (u.protocol !== 'https:') return false
    return TRUSTED_EMBED_PATHS.some(
      ([host, prefix]) => u.hostname === host && u.pathname.startsWith(prefix),
    )
  } catch {
    return false
  }
}
